'use client';
import {
  GetAssessmentProgressPayload,
  UserAssessmentProgress,
} from '@osmosis/shared/types/learndb/learnData';

import React, { PropsWithChildren, useContext } from 'react';
import { useQuery } from 'react-query';
import { useFetch } from './FetchProvider';
import { GetLearnPageResponse } from '../lib/learn';

type AssessmentProgressContext = {
  assessmentProgress?: UserAssessmentProgress;
  isLoading: boolean;
};

const AssessmentProgressContext =
  React.createContext<AssessmentProgressContext>({
    isLoading: true,
  });

type AssessmentProgressProps = {
  questions?: GetLearnPageResponse['sections']['questions'];
  flashcards?: GetLearnPageResponse['sections']['flashcards'];
  isLoggedInView: boolean;
};
/**
 * Provides the LearnData context.
 * @param props - The component props.
 */
export const AssessmentProgressProvider = ({
  questions,
  flashcards,
  isLoggedInView,
  children,
}: PropsWithChildren<AssessmentProgressProps>) => {
  const { post } = useFetch();

  const getAssessmentProgressPayload = (): GetAssessmentProgressPayload => {
    return {
      questions: questions
        ? {
            ...Object.keys(questions).reduce((acc, key) => {
              if (questions[key].list.length) {
                acc[key] = questions[key].list;
              }
              return acc;
            }, {} as Record<string, number[]>),
          }
        : {},
      flashcards: flashcards?.list.length ? flashcards?.list : [],
    };
  };

  const { data: assessmentProgress, isLoading } = useQuery(
    'assessmentProgress',
    async () => {
      if (!isLoggedInView) {
        return Promise.resolve(null);
      }
      const { data } = await post<
        UserAssessmentProgress,
        GetAssessmentProgressPayload
      >('/api/learn/assessment-progress', getAssessmentProgressPayload());

      return data;
    },
  );

  return (
    <AssessmentProgressContext.Provider
      value={
        {
          assessmentProgress,
          isLoading,
        } as AssessmentProgressContext
      }
    >
      {children}
    </AssessmentProgressContext.Provider>
  );
};

/**
 * Hook to access the user context.
 */
export const useAssessmentProgress = (): AssessmentProgressContext =>
  useContext(AssessmentProgressContext);
